import React from 'react';
import { ArrowLeft } from 'lucide-react';
import Header from '../Header';

/**
 * The bell's phone-safe placement in the trucking / rentals field shell
 * (fe#815, pinned by MyWorkLayout.bell.test.tsx). Below sm the panel spans the
 * screen with a 16px gutter and hangs from the header's bottom edge; from sm it
 * is the office dropdown. `fixed` measures from FieldHeader's wrapper, not the
 * viewport (its backdrop filter makes it the containing block), so 100% is the
 * header's own height.
 *
 * fe#815 pinned this 64px from the top of the old hand-built header. The shared
 * header carries the status-bar inset as top padding, about 47px on a notched
 * iPhone, which would have put that 64px line across the bell itself. Hanging
 * from the bottom edge cannot meet the bell, whatever the inset.
 */
export const FIELD_BELL_PANEL =
  'fixed left-4 right-4 top-full sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80';

/**
 * The casing field header for the trucking / rentals field shell: the one
 * shared <Header /> (logo + company, search, AI, bell, settings), never a copy.
 * The wrapper exists only to anchor the bell panel (see FIELD_BELL_PANEL) and
 * to keep that panel above the page below it.
 */
export function FieldHeader() {
  return (
    // DO NOT DROP THE BLUR CLASS: its backdrop filter is what makes this wrapper the
    // containing block for the bell panel's fixed position (FIELD_BELL_PANEL).
    // Without it the panel measures from the viewport and opens off-screen.
    // Pinned by MyWorkLayout.bell.test.tsx.
    <div data-testid="field-header" className="relative z-40 backdrop-blur">
      <Header bellPanelClassName={FIELD_BELL_PANEL} />
    </div>
  );
}

/**
 * The casing field sub-screen's title line: a back arrow and the title, under
 * the header. For a screen that lays out its own body (the weight ticket's
 * stepper) and for MyWorkLayout; everything else takes FieldPageChrome whole.
 */
export function FieldTitleRow({
  title,
  onBack,
  backLabel = 'Back',
  backTestId,
}: {
  title: React.ReactNode;
  onBack: () => void;
  backLabel?: string;
  backTestId?: string;
}) {
  return (
    <div className="px-4 py-3 border-b border-fieldiq-border flex items-center gap-3">
      <button type="button" data-testid={backTestId} onClick={onBack} aria-label={backLabel} className="text-fieldiq-text">
        <ArrowLeft size={26} aria-hidden="true" />
      </button>
      <h1 className="text-xl font-bold text-white">{title}</h1>
    </div>
  );
}

/**
 * A field screen in the trucking / rentals shell, in the casing field
 * sub-screen's shape (FieldTimeOffScreen, FieldPayScreen): the shared header,
 * then a back arrow and the title on one line, then the page. No office band
 * and no stat strip — a field hand opens these to do one thing.
 *
 * `action` is the screen's main action, drawn first in the body so it is the
 * first thing under the thumb (New Check-Out, New Inspection).
 */
export default function FieldPageChrome({
  title,
  onBack,
  action,
  testId,
  children,
}: {
  title: string;
  onBack: () => void;
  action?: React.ReactNode;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <div data-testid={testId} className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        <FieldHeader />
        <FieldTitleRow title={title} onBack={onBack} />
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-4">
        {action && <div data-testid="field-page-action">{action}</div>}
        {children}
      </div>
    </div>
  );
}
